<template>
  <div class="selected-requirements-panel">
    <div v-if="!requirements || requirements.length === 0" class="text-gray-500 italic p-4 text-center">
      <p>No requirements added yet. Add checks from the "Available Automated Checks" panel.</p>
    </div>
    <div v-else class="space-y-3">
      <template v-for="group in groupedRequirements" :key="group.key">
        <div class="policy-group-label">
          <span>{{ group.label }}</span>
          <span class="policy-group-count">{{ group.items.length }}</span>
        </div>
      <div v-for="(requirement, index) in group.items" :key="requirement.tempId || requirement.id || index"
           class="requirement-card p-4 bg-white rounded-lg shadow-md border border-gray-200">
        <div class="requirement-header flex justify-between items-start">
          <div class="flex-grow pr-4">
            <h4 class="font-semibold text-gray-800 text-md">{{ requirement.name }}</h4>
            <p class="text-sm text-gray-600">
              <span class="policy-chip">{{ policyChipLabel(requirement) }}</span>
              Order: {{ requirement.order }} | Severity: {{ requirement.severity }}
            </p>
            <p v-if="requirement.automated_check_details && requirement.automated_check_details.name" class="text-xs text-gray-500 mt-1">
              <i class="fas fa-link mr-1"></i>Based on: <span class="font-medium">{{ requirement.automated_check_details.name }}</span>
            </p>
            <p v-if="requirement.description" class="text-xs text-gray-500 mt-1 truncate" :title="requirement.description">
              {{ requirement.description }}
            </p>
          </div>
          <div class="flex items-center space-x-1 flex-shrink-0">
            <button @click="toggleEdit(group.key, index)" 
                    class="p-2 text-blue-500 hover:text-blue-700 rounded-full hover:bg-blue-50 transition-colors"
                    title="Edit Classification Filters">
              <i class="fas fa-filter"></i>
            </button>
            <button @click="moveRequirement(group.key, index, -1)" :disabled="index === 0"
                    class="p-2 text-gray-500 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-full hover:bg-gray-100 transition-colors"
                    title="Move Up">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd"></path></svg>
            </button>
            <button @click="moveRequirement(group.key, index, 1)" :disabled="index === group.items.length - 1"
                    class="p-2 text-gray-500 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-full hover:bg-gray-100 transition-colors"
                    title="Move Down">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
            </button>
            <button @click="removeRequirement(group.key, index)"
                    class="p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50 transition-colors"
                    title="Remove Requirement">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>
            </button>
          </div>
        </div>
        
        <!-- Classification Filters Editor (Collapsible) -->
        <div v-if="isEditing(group.key, index)" class="classification-filters mt-4 p-4 bg-gray-50 rounded-md border border-gray-300">
          <h5 class="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <i class="fas fa-filter text-blue-500"></i>
            Classification Filters
            <span class="text-xs font-normal text-gray-500">(Leave empty to apply to all types)</span>
          </h5>
          
          <!-- Document Types Filter -->
          <div class="form-group mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              📄 Applicable Document Types
            </label>
            <div class="checkbox-group">
              <label v-for="docType in documentTypes" :key="docType.value" class="checkbox-label">
                <input 
                  type="checkbox" 
                  :value="docType.value"
                  :checked="requirement.applicable_document_types && requirement.applicable_document_types.includes(docType.value)"
                  @change="toggleDocumentType(group.key, index, docType.value)"
                />
                <span>{{ docType.label }}</span>
              </label>
            </div>
            <small class="text-gray-500 text-xs block mt-2">
              Select specific document types this requirement applies to. Leave unchecked to run on all types.
            </small>
          </div>
          
          <!-- Coverage Types Filter -->
          <div class="form-group mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              🛡️ Applicable Coverage Types
            </label>
            <div class="checkbox-group">
              <label v-for="coverage in coverageTypes" :key="coverage.value" class="checkbox-label">
                <input 
                  type="checkbox" 
                  :value="coverage.value"
                  :checked="requirement.applicable_coverage_types && requirement.applicable_coverage_types.includes(coverage.value)"
                  @change="toggleCoverageType(group.key, index, coverage.value)"
                />
                <span>{{ coverage.label }}</span>
              </label>
            </div>
            <small class="text-gray-500 text-xs block mt-2">
              Select specific coverage types this requirement applies to (for insurance documents). Leave unchecked for all.
            </small>
          </div>
          
          <!-- Require Classification Flag -->
          <div class="form-group">
            <label class="checkbox-label-block">
              <input 
                type="checkbox" 
                :checked="requirement.require_classification"
                @change="toggleRequireClassification(group.key, index)"
              />
              <span class="font-medium">Require AI classification before running this check</span>
            </label>
            <small class="text-gray-500 text-xs block mt-1 ml-6">
              If enabled, document must be successfully classified before this requirement runs.
            </small>
          </div>
          
          <div class="flex justify-end mt-3">
            <button @click="editingKey = null" class="btn-sm btn-secondary">
              Done
            </button>
          </div>
        </div>
      </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { groupRequirementsByPolicy, policyChipLabel } from '@/utils/requirementPolicyGroups';

// eslint-disable-next-line no-undef
const props = defineProps({
  requirements: {
    type: Array,
    required: true,
    default: () => []
  }
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['update-requirements']);

const editingKey = ref(null);

const groupedRequirements = computed(() => groupRequirementsByPolicy(props.requirements));

function itemAt(groupKey, indexInGroup) {
  const group = groupedRequirements.value.find((g) => g.key === groupKey);
  return group?.items[indexInGroup] || null;
}

function originalIndexOf(item) {
  return props.requirements.findIndex((r) =>
    (r.id && item.id && r.id === item.id) || (r.tempId && item.tempId && r.tempId === item.tempId)
  );
}

function editKey(groupKey, index) {
  return `${groupKey}:${index}`;
}

function isEditing(groupKey, index) {
  return editingKey.value === editKey(groupKey, index);
}

// Document types
const documentTypes = [
  { value: 'CERTIFICATE', label: 'Certificate of Insurance' },
  { value: 'INSURANCE_POLICY', label: 'Insurance Policy' },
  { value: 'ENDORSEMENT', label: 'Policy Endorsement' },
  { value: 'LOSS_RUN', label: 'Loss Run Report' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'AGREEMENT', label: 'Agreement' },
  { value: 'AMENDMENT', label: 'Amendment' },
  { value: 'QUOTE', label: 'Quote/Proposal' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'W9_FORM', label: 'W-9 Form' },
  { value: 'TAX_FORM', label: 'Tax Form' },
  { value: 'OTHER', label: 'Other' }
];

// Coverage types (insurance-specific)
const coverageTypes = [
  { value: 'GENERAL_LIABILITY', label: 'General Liability (GL)' },
  { value: 'WORKERS_COMP', label: 'Workers Compensation (WC)' },
  { value: 'AUTO_LIABILITY', label: 'Auto Liability (AL)' },
  { value: 'UMBRELLA', label: 'Umbrella/Excess' },
  { value: 'PROFESSIONAL_LIABILITY', label: 'Professional Liability (E&O)' },
  { value: 'PROPERTY', label: 'Property' },
  { value: 'CYBER', label: 'Cyber Liability' },
  { value: 'POLLUTION', label: 'Pollution Liability' },
  { value: 'BUILDERS_RISK', label: 'Builders Risk' },
  { value: 'INLAND_MARINE', label: 'Inland Marine' },
  { value: 'EMPLOYMENT_PRACTICES', label: 'Employment Practices (EPLI)' },
  { value: 'DIRECTORS_OFFICERS', label: 'Directors & Officers (D&O)' }
];

function moveRequirement(groupKey, indexInGroup, direction) {
  const itemToMove = itemAt(groupKey, indexInGroup);
  const neighbor = itemAt(groupKey, indexInGroup + direction);
  if (!itemToMove || !neighbor) return;

  const newRequirements = [...props.requirements];
  const moveIdx = originalIndexOf(itemToMove);
  const swapIdx = originalIndexOf(neighbor);
  if (moveIdx === -1 || swapIdx === -1) return;

  const tempOrder = newRequirements[moveIdx].order;
  newRequirements[moveIdx] = { ...newRequirements[moveIdx], order: newRequirements[swapIdx].order };
  newRequirements[swapIdx] = { ...newRequirements[swapIdx], order: tempOrder };
  emit('update-requirements', newRequirements);
}

function removeRequirement(groupKey, indexInGroup) {
  const itemToRemove = itemAt(groupKey, indexInGroup);
  if (!itemToRemove) return;
  const newRequirements = props.requirements.filter((r) =>
    !((r.id && itemToRemove.id && r.id === itemToRemove.id)
      || (r.tempId && itemToRemove.tempId && r.tempId === itemToRemove.tempId))
  );
  emit('update-requirements', newRequirements);
}

function toggleEdit(groupKey, index) {
  const key = editKey(groupKey, index);
  editingKey.value = editingKey.value === key ? null : key;
}

function mutateRequirement(groupKey, indexInGroup, mutator) {
  const requirement = itemAt(groupKey, indexInGroup);
  const originalIndex = requirement ? originalIndexOf(requirement) : -1;
  if (originalIndex === -1) return;

  const newRequirements = [...props.requirements];
  const req = { ...newRequirements[originalIndex] };
  mutator(req);
  newRequirements[originalIndex] = req;
  emit('update-requirements', newRequirements);
}

function toggleDocumentType(groupKey, indexInGroup, docType) {
  mutateRequirement(groupKey, indexInGroup, (req) => {
    const types = [...(req.applicable_document_types || [])];
    const typeIndex = types.indexOf(docType);
    if (typeIndex === -1) types.push(docType);
    else types.splice(typeIndex, 1);
    req.applicable_document_types = types;
  });
}

function toggleCoverageType(groupKey, indexInGroup, coverage) {
  mutateRequirement(groupKey, indexInGroup, (req) => {
    const types = [...(req.applicable_coverage_types || [])];
    const coverageIndex = types.indexOf(coverage);
    if (coverageIndex === -1) types.push(coverage);
    else types.splice(coverageIndex, 1);
    req.applicable_coverage_types = types;
  });
}

function toggleRequireClassification(groupKey, indexInGroup) {
  mutateRequirement(groupKey, indexInGroup, (req) => {
    req.require_classification = !req.require_classification;
  });
}

</script>

<style scoped>
.policy-group-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.35rem 0.15rem 0;
  margin-top: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.policy-group-label:first-child {
  margin-top: 0;
}

.policy-group-count {
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #94a3b8;
}

.policy-chip {
  display: inline-block;
  margin-right: 0.4rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.requirement-card {
  transition: all 0.2s ease;
}

.requirement-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.classification-filters {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 800px;
  }
}

.checkbox-group {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.checkbox-label:hover {
  background-color: #f3f4f6;
}

.checkbox-label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.checkbox-label span {
  font-size: 0.875rem;
  color: #374151;
}

.checkbox-label-block {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-sm:hover {
  background: #f9fafb;
}

.btn-secondary {
  color: #4b5563;
}
</style> 